import { getGeneralPercentage } from '../../api/settings';
import { getDailySessions, updateSession, removeSession } from '../../api/sessions';

/**
 * Fetches and filters sessions for the authenticated user for a specific day.
 * @param {string} dateKey - Date in YYYY-MM-DD format
 * @returns {number} The amount removed from sessions
 * @throws {Error} If the request fails
 */
async function processSessions(dateKey) {
    try {
        // Fetch all sessions for the day
        const sessions = await getDailySessions(dateKey);
        console.log('Sessions before filtering:', sessions);

        if (!sessions || sessions.length === 0) {
            console.log('No sessions found.');
            return 0;
        }

        // Combine all items, extras, and services into a single list with references
        const allElements = sessions.flatMap((session) => {
            return [
                ...session.items.map(item => ({ 
                    type: 'item', 
                    name: item.name, 
                    price: item.price, 
                    sessionId: session.id 
                })),
                ...session.extras.map(extra => ({ 
                    type: 'extra', 
                    name: extra.name, 
                    price: extra.price, 
                    sessionId: session.id 
                })),
                ...session.services.map(service => ({ 
                    type: 'service', 
                    name: service.name, 
                    price: service.price, 
                    sessionId: session.id 
                })),
            ];
        });

        // Sort all elements by price ascending
        const sortedElements = allElements.sort((a, b) => a.price - b.price);
        console.log('Sorted elements (ascending):', sortedElements);

        // Calculate the total price across all sessions
        const totalPrice = sessions.reduce((sum, session) => sum + session.total_price, 0);
        console.log('Starting total price:', totalPrice);

        // Fetch the percentage from the database
        const selectedPercentage = await getGeneralPercentage();
        console.log(`Fetched percentage: ${selectedPercentage}%`);

        // Calculate the target reduction based on the percentage
        const targetReduction = (totalPrice * selectedPercentage) / 100;
        console.log(`Target reduction: ${targetReduction} EUR`);

        let removedTotal = 0;
        let remainingSessions = [...sessions];
        const modifiedSessions = new Map();

        // Remove elements one by one until we approach the target
        for (let i = 0; i < sortedElements.length; i++) {
            const element = sortedElements[i];
            const nextElement = sortedElements[i + 1];

            // Check if adding the current element exceeds or gets us close to target
            const potentialTotal = removedTotal + element.price;
            const nextPotentialTotal = nextElement ? potentialTotal + nextElement.price : potentialTotal;

            // Decide whether to include this element based on what's closer to target
            if (potentialTotal > targetReduction) {
                const undershoot = targetReduction - removedTotal;
                const overshoot = potentialTotal - targetReduction;
                
                if (undershoot <= overshoot) {
                    break; // Stop here, staying under is closer to target
                }
            }

            const sessionIndex = remainingSessions.findIndex(s => s.id === element.sessionId);
            if (sessionIndex === -1) continue;

            const session = remainingSessions[sessionIndex];

            // Remove the element from its respective array
            if (element.type === 'item') {
                session.items = session.items.filter(item => item.name !== element.name);
            } else if (element.type === 'extra') {
                session.extras = session.extras.filter(extra => extra.name !== element.name);
            } else if (element.type === 'service') {
                session.services = session.services.filter(service => service.name !== element.name);
            }

            // Recalculate session total price
            session.total_price = session.items.reduce((sum, item) => sum + item.price, 0) +
                                session.extras.reduce((sum, extra) => sum + extra.price, 0) +
                                session.services.reduce((sum, service) => sum + service.price, 0);

            // Check if session is now empty
            if (session.items.length === 0 && 
                session.extras.length === 0 && 
                session.services.length === 0) {
                console.log(`Session ${session.id} is now empty and will be removed from the database.`);
                await removeSession(session.id); // Use the new removeSession function
                remainingSessions.splice(sessionIndex, 1); // Remove from local array
            } else {
                modifiedSessions.set(session.id, session); // Update session if not empty
            }

            removedTotal += element.price;
            console.log(`Removed ${element.type}: ${element.name} for ${element.price} EUR from session ${session.id}. Total removed so far: ${removedTotal} EUR`);
        }

        // Update modified sessions in the database
        for (const [sessionId, session] of modifiedSessions) {
            const sessionData = {
                barberName: session.barber_name,
                services: session.services,
                extras: session.extras,
                items: session.items,
                totalPrice: session.total_price,
                tip: session.tip || 0,
                duration: session.duration,
            };
            await updateSession(sessionId, sessionData);
            console.log(`Updated session ${sessionId} in the database.`);
        }

        // Final calculations and logging
        const filteredTotalPrice = remainingSessions.reduce((sum, session) => sum + session.total_price, 0);
        const priceDifference = totalPrice - filteredTotalPrice;
        console.log('Final total price:', filteredTotalPrice);
        console.log('Difference in price:', priceDifference);
        console.log('Sessions after filtering:', remainingSessions);

        if (Math.abs(targetReduction - removedTotal) < 0.01) {
            console.log('Successfully removed the exact target amount from sessions.');
        } else {
            console.log(`Actually removed ${(removedTotal / totalPrice * 100).toFixed(2)}% of the total price.`);
        }

        return priceDifference;
    } catch (error) {
        console.error('processSessions error:', error.message);
        throw error; // Re-throw to allow caller to handle
    }
}

export { processSessions };